import { useState, useEffect } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import { useProjectContext } from "../../context/useProjectContext";
import { showGenericError } from "../../helper/Notifications";
import { useOutletContext } from "react-router-dom";
import { useLicensingContext } from "../../context/useLicensingContext";
import { usePresentationContext } from "../../context/usePresentationContext";
import SubscriptionCards from "../SubscriptionsPage/SubscriptionCards";
import Toggle from "../../Components/Generic/Toggle";
import Dropdown from "../../Components/Generic/Dropdown";
import PrimaryButton from "../../Components/Generic/PrimaryButton";
import { CURRENCY_OPTIONS, webAppUrl, STATUS_CONFIG, PRESENTATIONS} from "../../constants/OptionsConstants";
import { getAuthBridgeExchangeToken } from "../../api/authBridge";
import LocalStorage from "../../helper/LocalStorage";
import { Presentation } from "lucide-react";
import Pill from "../../Components/Generic/Pill";
import useMemberProductAccess from "../../hooks/useMemberProductAccess";
import MemberAccessBanner from "../../Components/Generic/MemberAccessBanner";

import StyledTable, {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/Generic/StyledTable";
import TablePagination from "../../Components/Generic/TablePagination";
import usePurchaseSubscription from "../../hooks/usePurchaseSubscription";
import AppDateRangePicker from "../../Components/Generic/AppDateRangePicker";
import PageHeader from "../../Components/Layout/PageHeader";
import ProductUsageWidget from "../../Components/Generic/ProductUsageWidget";
import { getDefaultMonthToDateRange } from "../../helper/dateFormat";

const PresentationsPage = () => {
  const { selectedProject } = useProjectContext();
  const { setLoading, setShowSalesModal } = useOutletContext();
  const { subscription } = useLicensingContext();
  const { presentationJobs, isPresentationsLoading, totalPages, loadPresentationJobs } = usePresentationContext();
  const {
    handlePurchase,
    annualPricing,
    setAnnualPricing,
    effectiveAnnual,
    billingInterval,
  } = usePurchaseSubscription();
  const { hasProductAccess: hasMemberPresentationsAccess } = useMemberProductAccess("presentations");

  const getStatusConfig = (status) => {
  if (!status || status === "-") return null;
  return STATUS_CONFIG[status.toLowerCase()] || STATUS_CONFIG['default'];
};

  const [dateRange, setDateRange] = useState(getDefaultMonthToDateRange);

  const [currency, setCurrency] = useState(() => subscription?.currency || "GBP");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const presentationsItem = subscription?.items?.find(
    (i) => i.product_slug === "presentations"
  );
  const hasPresentations = !!presentationsItem || hasMemberPresentationsAccess;

  useEffect(() => {
    if (!hasPresentations || !selectedProject?.id) return;
    const [start, end] = dateRange;
    const startDate = new Date(start);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(end);
    endDate.setHours(23, 59, 59, 999);
    loadPresentationJobs(page, itemsPerPage, {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    });
  }, [selectedProject?.id, hasPresentations, page, itemsPerPage, dateRange]);

  useEffect(() => {
    if (subscription?.currency) {
      setCurrency(subscription.currency);
    }
  }, [subscription]);

  if (subscription === undefined) return null;

  return (
    <Container>
      <PageContent>
        <Header>
          <HeaderContent>
            <PageHeader
              title={hasPresentations ? PRESENTATIONS : `${PRESENTATIONS} Plans`}
              subtitle={(hasPresentations)
                ? "Your plan includes AI-powered presentation translation."
                : "Choose a plan to unlock AI-powered presentation translation for your team."}
            />
          </HeaderContent>
          <HeaderRight>
            {hasPresentations && (
              <PrimaryButton
                icon={<Presentation size={20} strokeWidth={2}/>}
                text={"Translate Presentation"}
                onClick={async () => {
                  const projectParam = selectedProject?.id ? `&project_id=${selectedProject.id}` : "";
                  const fallbackUrl = `${webAppUrl}/#/?section=tools&tool=presentations${projectParam}`;
                  const currentToken = LocalStorage.getAuthenticationToken();
                  if (currentToken) {
                    const exchangeToken = await getAuthBridgeExchangeToken(currentToken);
                    if (exchangeToken) {
                      window.open(`${webAppUrl}/#/login?bridge=${exchangeToken}`, "_blank", "noopener,noreferrer");
                      return;
                    }
                  }
                  window.open(fallbackUrl, "_blank", "noopener,noreferrer");
                }}
              />
            )}
            {!hasPresentations && (
              <Dropdown
                value={currency}
                onValueChange={setCurrency}
                size="default"
                options={CURRENCY_OPTIONS}
                disabled={!!billingInterval}
              />
            )}
          </HeaderRight>
        </Header>
        {hasPresentations && <MemberAccessBanner />}
        {hasPresentations && selectedProject && (
          <ProductUsageWidget 
            projectId={selectedProject.id} 
            productSlug="presentations" 
            dateRange={dateRange}
            calendarAction={
              <AppDateRangePicker 
                value={dateRange} 
                onChange={(newRange) => setDateRange(newRange)} 
              />
            }
          />
        )}

        {hasPresentations && (
          <>
          <StyledTable>
            <TableHeader>
              <TableRow>
                <TableHead style={{ textAlign: "left", width: "22%" }}>Name</TableHead>
                <TableHead style={{ textAlign: "center" }}>Status</TableHead>
                <TableHead style={{ textAlign: "center" }}>Words</TableHead>
                <TableHead style={{ textAlign: "center" }}>Languages</TableHead>
                <TableHead style={{ textAlign: "center" }}>User</TableHead>
                <TableHead style={{ textAlign: "center" }}>Translated on</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {isPresentationsLoading ? (
                <TableRow>
                  <TableCell colSpan={5} style={{ textAlign: "center", padding: "40px" }}>
                    Loading presentations...
                  </TableCell>
                </TableRow>
              ) : presentationJobs && presentationJobs.length > 0 ? (
                presentationJobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>{job.presentation_name || "—"}</TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                    {(() => {
                    const config = getStatusConfig(job.status);

                    return config ? (
                   <Pill variant={config.variant} size="sm">{config.label}</Pill>
                    ) : (
                   <span>-</span>
                     );
                     })()}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {(job.word_count || 0).toLocaleString()}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {Array.isArray(job.target_languages)
                        ? job.target_languages.join(", ")
                        : job.target_languages || "—"}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      <TruncatedText title={job.user_label || undefined}>
                        {job.user_label || "—"}
                      </TruncatedText>
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {job.created_at ? new Date(job.created_at).toLocaleDateString() : "—"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} style={{ textAlign: "center", padding: "40px 20px" }}>
                    <NoDataText>No presentations translated yet. Click "Translate Presentation" to get started.</NoDataText>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

            <TablePagination
              page={page}
              totalPages={totalPages}
              onPrevious={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => p + 1)}
              leftColSpan={2}
              centerColSpan={2}
              rightColSpan={2}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(v) => { setItemsPerPage(v); setPage(1); }}
            />
          </StyledTable>
          </>
        )}

        {!hasPresentations && (
          <PlanCardsSection>
            <PlanCardsHeader>
              <PricingLabel>Pricing plans</PricingLabel>
              {!billingInterval && (
                <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                  <Toggle
                    checked={annualPricing}
                    onCheckedChange={setAnnualPricing}
                  />
                  <span className="font-inter text-[16px] leading-6 font-medium text-brand-900">
                    Annual pricing{" "}
                    <span className="text-brand-700 font-medium">(save 10%)</span>
                  </span>
                </label>
              )}
            </PlanCardsHeader>
            <SubscriptionCards
              purchaseSubscription={handlePurchase}
              contactSales={() => setShowSalesModal(true)}
              annual={effectiveAnnual}
              currency={currency}
              product="presentations"
            />
          </PlanCardsSection>
        )}
      </PageContent>
    </Container>
  );
};

export default PresentationsPage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  padding: 30px 30px 30px 30px;
  height: 100%;
  background: #f9fafb;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  background: white;
  margin: -40px -30px 0 -30px;
  padding: 30px 30px 10px 30px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const PlanCardsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const PlanCardsHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const PricingLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 2px 12px;
  border-radius: 16px;
  background: #f4ebff;
  color: #6941c6;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`;

const NoDataText = styled.div`
  color: #667085;
  font-size: 14px;
  font-weight: 400;
`;

const TruncatedText = styled.span`
  display: block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  color: #344054;
`;
